import { useEffect, useRef } from "react";

export type CrmNewLeadEvent = {
	type: "NEW_LEAD";
	recipientStaffIds?: string[];
	lead: {
		id: string;
		patientName: string;
		phone?: string | null;
		source: string;
		campaignName?: string | null;
		assignedTo?: { id: string; name: string; managerId?: string | null } | null;
		createdAt: string;
	};
};

export type CrmSarvamReminderConfirmedEvent = {
	type: "SARVAM_REMINDER_CONFIRMED";
	recipientStaffIds?: string[];
	sarvamLeadCallId?: string;
	appointmentAt?: string | null;
	appointmentLabel?: string | null;
	confirmedAt: string;
	lead: {
		id: string;
		patientName: string;
		phone?: string | null;
		source: string;
		campaignName?: string | null;
		assignedTo?: { id: string; name: string; managerId?: string | null } | null;
	};
};

export type CrmLeadActivityEvent = {
	type: "LEAD_ACTIVITY";
	leadId: string;
	at?: string;
};

export type CrmWsMessage =
	| CrmNewLeadEvent
	| CrmSarvamReminderConfirmedEvent
	| CrmLeadActivityEvent
	| { type: "CONNECTED"; at: string }
	| { type: "PONG"; at: string };

const LAST_SEEN_KEY = "thms:crm:new-lead:lastSeenAt";
const MAX_BACKOFF_MS = 30_000;

type Listener = (data: CrmWsMessage) => void;
const listeners = new Set<Listener>();
let connectionSubscribers = 0;
let ws: WebSocket | null = null;
let closed = false;
let reconnectTimer: number | null = null;
let attempt = 0;
let pingTimer: number | null = null;

function apiBaseToWsUrl(token: string, since?: string | null): string {
	const httpBase = (
		import.meta.env.VITE_API_BASE_URL ||
		import.meta.env.VITE_BACKEND_URL ||
		window.location.origin
	).replace(/\/$/, "");

	let wsOrigin: string;
	try {
		const u = new URL(httpBase);
		u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
		wsOrigin = u.origin;
	} catch {
		const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
		wsOrigin = `${proto}//${window.location.host}`;
	}

	const params = new URLSearchParams({ token });
	if (since) params.set("since", since);
	return `${wsOrigin}/ws/crm?${params.toString()}`;
}

export function getNewLeadLastSeenAt(): string | null {
	try {
		return localStorage.getItem(LAST_SEEN_KEY);
	} catch {
		return null;
	}
}

export function setNewLeadLastSeenAt(iso: string) {
	try {
		localStorage.setItem(LAST_SEEN_KEY, iso);
	} catch {
		/* ignore */
	}
}

function clearTimers() {
	if (reconnectTimer != null) {
		window.clearTimeout(reconnectTimer);
		reconnectTimer = null;
	}
	if (pingTimer != null) {
		window.clearInterval(pingTimer);
		pingTimer = null;
	}
}

function dispatchMessage(data: CrmWsMessage) {
	for (const listener of listeners) {
		try {
			listener(data);
		} catch {
			/* ignore subscriber errors */
		}
	}
}

function scheduleReconnect() {
	if (closed || connectionSubscribers <= 0) return;
	clearTimers();
	const delay = Math.min(1000 * 2 ** attempt, MAX_BACKOFF_MS);
	attempt += 1;
	reconnectTimer = window.setTimeout(() => connect(), delay);
}

function connect() {
	if (closed || connectionSubscribers <= 0) return;
	const token = localStorage.getItem("token");
	if (!token) {
		scheduleReconnect();
		return;
	}

	try {
		ws?.close();
	} catch {
		/* ignore */
	}

	const since = getNewLeadLastSeenAt();
	const url = apiBaseToWsUrl(token, since);
	ws = new WebSocket(url);

	ws.onopen = () => {
		attempt = 0;
		const lastSeen = getNewLeadLastSeenAt();
		if (lastSeen && ws?.readyState === WebSocket.OPEN) {
			try {
				ws.send(JSON.stringify({ type: "HELLO", lastSeenAt: lastSeen }));
			} catch {
				/* ignore */
			}
		}
		pingTimer = window.setInterval(() => {
			if (ws?.readyState === WebSocket.OPEN) {
				try {
					ws.send(JSON.stringify({ type: "PING" }));
				} catch {
					/* ignore */
				}
			}
		}, 25_000);
	};

	ws.onmessage = (ev) => {
		try {
			const data = JSON.parse(String(ev.data)) as CrmWsMessage;
			if (data?.type === "NEW_LEAD" && data.lead?.id) {
				const createdAt = data.lead.createdAt || new Date().toISOString();
				const prev = getNewLeadLastSeenAt();
				if (!prev || Date.parse(createdAt) >= Date.parse(prev)) {
					setNewLeadLastSeenAt(createdAt);
				}
			}
			dispatchMessage(data);
		} catch {
			/* ignore */
		}
	};

	ws.onclose = () => {
		clearTimers();
		if (!closed && connectionSubscribers > 0) scheduleReconnect();
	};

	ws.onerror = () => {
		try {
			ws?.close();
		} catch {
			/* ignore */
		}
	};
}

function acquireConnection() {
	connectionSubscribers += 1;
	if (connectionSubscribers === 1) {
		closed = false;
		connect();
	}
}

function releaseConnection() {
	connectionSubscribers = Math.max(0, connectionSubscribers - 1);
	if (connectionSubscribers === 0) {
		closed = true;
		clearTimers();
		try {
			ws?.close();
		} catch {
			/* ignore */
		}
		ws = null;
	}
}

/**
 * Shared CRM WebSocket — subscribe to NEW_LEAD and/or SARVAM_REMINDER_CONFIRMED.
 */
export function useCrmWebSocket(options: {
	enabled: boolean;
	onNewLead?: (event: CrmNewLeadEvent) => void;
	onSarvamReminderConfirmed?: (event: CrmSarvamReminderConfirmedEvent) => void;
	onLeadActivity?: (leadId: string) => void;
}) {
	const { enabled, onNewLead, onSarvamReminderConfirmed, onLeadActivity } = options;
	const onNewLeadRef = useRef(onNewLead);
	const onReminderRef = useRef(onSarvamReminderConfirmed);
	const onLeadActivityRef = useRef(onLeadActivity);
	onNewLeadRef.current = onNewLead;
	onReminderRef.current = onSarvamReminderConfirmed;
	onLeadActivityRef.current = onLeadActivity;

	useEffect(() => {
		if (!enabled) return;

		const listener: Listener = (data) => {
			if (data?.type === "NEW_LEAD" && data.lead?.id && onNewLeadRef.current) {
				onNewLeadRef.current(data);
			}
			if (
				data?.type === "SARVAM_REMINDER_CONFIRMED" &&
				data.lead?.id &&
				onReminderRef.current
			) {
				onReminderRef.current(data);
			}
			if (data?.type === "LEAD_ACTIVITY" && data.leadId && onLeadActivityRef.current) {
				onLeadActivityRef.current(data.leadId);
			}
		};

		listeners.add(listener);
		acquireConnection();

		return () => {
			listeners.delete(listener);
			releaseConnection();
		};
	}, [enabled]);
}
